// Canonical copy: src/shared/success/successRecords.ts. Mirrored byte-for-byte at functions/src/shared/successRecords.ts — edit both (enforced by successRecords.mirror.test.ts).
// Classifies raw `client_success` docs. No imports, so the same file runs in the web app and in Cloud Functions.

export const SUCCESS_UPDATE_INTERVAL_DAYS = 42;
export const LEGACY_PLANNED_COMMENT = "Auto-created from achieved goals/targets";

export type SuccessRecordKind = "update" | "planned" | "assessment_seed";

export interface SuccessRecordSummary {
  id: string;
  clientId: string;
  origin: SuccessRecordKind;
  kind: SuccessRecordKind;
  hasContent: boolean;
  activityAt: Date | null;
  createdAt: Date | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const UPDATE_TEXT_FIELDS = ["goal_update", "target1_update", "target2_update", "target3_update"];
const ACHIEVED_FIELDS = ["goal_achieved", "target1_achieved", "target2_achieved", "target3_achieved"];

function validDate(date: Date): Date | null {
  return isNaN(date.getTime()) ? null : date;
}

// Accepts a Firestore Timestamp (web or admin), a {seconds, nanoseconds} map, an ISO string,
// epoch milliseconds or a Date. Anything that doesn't produce a valid date gives null.
export function coerceDate(value: unknown): Date | null {
  if (value instanceof Date) return validDate(value);
  if (typeof value === "string" || typeof value === "number") return validDate(new Date(value));
  if (value !== null && typeof value === "object") {
    const candidate = value as { toDate?: unknown; seconds?: unknown; nanoseconds?: unknown };
    if (typeof candidate.toDate === "function") {
      const converted = candidate.toDate();
      return converted instanceof Date ? validDate(converted) : null;
    }
    if (typeof candidate.seconds === "number") {
      const nanoseconds = typeof candidate.nanoseconds === "number" ? candidate.nanoseconds : 0;
      return validDate(new Date(candidate.seconds * 1000 + Math.floor(nanoseconds / 1e6)));
    }
  }
  return null;
}

function isNonBlank(value: unknown): boolean {
  return typeof value === "string" && value.trim() !== "";
}

export function hasSuccessUpdateContent(data: Record<string, unknown>): boolean {
  if (UPDATE_TEXT_FIELDS.some((field) => isNonBlank(data[field]))) return true;
  if (ACHIEVED_FIELDS.some((field) => data[field] === true)) return true;
  const comments = data.comments;
  return typeof comments === "string" && comments.replace(LEGACY_PLANNED_COMMENT, "").trim() !== "";
}

// What the record was created as, before looking at whether a coach filled it in.
function originOf(data: Record<string, unknown>): SuccessRecordKind {
  const stored = data.kind;
  if (stored === "update" || stored === "planned" || stored === "assessment_seed") return stored;
  if (Object.prototype.hasOwnProperty.call(data, "id")) return "assessment_seed";
  if (typeof data.comments === "string" && data.comments.includes("Auto-created")) return "planned";
  return "update";
}

export function summarizeSuccessRecord(id: string, data: Record<string, unknown>): SuccessRecordSummary {
  const origin = originOf(data);
  const hasContent = hasSuccessUpdateContent(data);
  const createdAt = coerceDate(data.created_at);
  const updatedAt = coerceDate(data.updated_at);
  return {
    id,
    clientId: data.client_id === undefined || data.client_id === null ? "" : String(data.client_id),
    origin,
    kind: origin !== "update" && hasContent ? "update" : origin,
    hasContent,
    activityAt: updatedAt ?? createdAt,
    createdAt: createdAt ?? updatedAt,
  };
}

// Newest record by date; a record without a date sorts as the oldest. Never mutates `records`.
function newestBy<T>(records: T[], dateOf: (record: T) => Date | null): T | null {
  let newest: T | null = null;
  let newestTime = -Infinity;
  for (let i = 0; i < records.length; i++) {
    const date = dateOf(records[i]);
    const time = date === null ? -Infinity : date.getTime();
    if (newest === null || time > newestTime) {
      newest = records[i];
      newestTime = time;
    }
  }
  return newest;
}

export function latestCountedRecord<T extends { kind: SuccessRecordKind; activityAt: Date | null }>(records: T[]): T | null {
  return newestBy(records.filter((record) => record.kind === "update"), (record) => record.activityAt);
}

export function findFillableRecord<T extends { kind: SuccessRecordKind; createdAt: Date | null }>(records: T[]): T | null {
  const newest = newestBy(records, (record) => record.createdAt);
  return newest !== null && newest.kind !== "update" ? newest : null;
}

export function latestCountedActivityByClient(docs: Array<{ id: string; data: Record<string, unknown> }>): Map<string, Date> {
  const latest = new Map<string, Date>();
  docs.forEach((doc) => {
    const summary = summarizeSuccessRecord(doc.id, doc.data);
    if (summary.kind !== "update" || summary.clientId === "" || summary.activityAt === null) return;
    const current = latest.get(summary.clientId);
    if (current === undefined || summary.activityAt.getTime() > current.getTime()) {
      latest.set(summary.clientId, summary.activityAt);
    }
  });
  return latest;
}

export function nextSuccessUpdateDue(
  records: Array<{ kind: SuccessRecordKind; activityAt: Date | null }>,
  firstSessionDate: Date | null
): Date | null {
  const latest = latestCountedRecord(records);
  const base = latest?.activityAt ?? coerceDate(firstSessionDate);
  return base === null ? null : new Date(base.getTime() + SUCCESS_UPDATE_INTERVAL_DAYS * DAY_MS);
}

export const SUCCESS_DUE_SOON_DAYS = 7;

export type SuccessUpdateStatus = "overdue" | "awaiting_first" | "due_soon" | "on_track" | "not_yet_due" | "no_first_session";

// Strict boundary: exactly 42 days since the last update is still due soon, not overdue.
// Update history wins; without any, the first session date decides awaiting_first vs not_yet_due.
export function successUpdateStatus(latest: Date | null, firstSession: Date | null, now: Date): SuccessUpdateStatus {
  const intervalMs = SUCCESS_UPDATE_INTERVAL_DAYS * DAY_MS;
  if (latest !== null) {
    const dueInMs = latest.getTime() + intervalMs - now.getTime();
    if (dueInMs < 0) return "overdue";
    return dueInMs <= SUCCESS_DUE_SOON_DAYS * DAY_MS ? "due_soon" : "on_track";
  }
  if (firstSession === null) return "no_first_session";
  return firstSession.getTime() < now.getTime() - intervalMs ? "awaiting_first" : "not_yet_due";
}

// Whole days past the due date; an overdue client always reads as at least 1 day.
export function daysOverdue(latest: Date, now: Date): number {
  const dueAt = latest.getTime() + SUCCESS_UPDATE_INTERVAL_DAYS * DAY_MS;
  return Math.max(1, Math.floor((now.getTime() - dueAt) / DAY_MS));
}
